import { ListChatsPaginationDocument } from '@graphql/generated';

import type { ApolloCache } from '@apollo/client';
import type {
  DocumentNode,
  FieldNode,
  OperationDefinitionNode,
  SelectionSetNode,
} from 'graphql';

// TA-10627: fields a chat list row needs before it can render as anything
// other than a blank row.
//
// Nothing that feeds this helper writes a complete Chat. The `chatUpdate`
// subscription returns the `ChatUpdate` type — a different __typename, so
// Apollo never normalizes it onto the Chat entity at all — and `markChatsRead`
// returns a Chat with only { id, unreadCount }. Prepending a reference to
// either left a row with no name, a fallback red avatar and a raw
// `{{val, timeAgoChatList}}` timestamp, which still opened a real chat when
// clicked.
//
// TA-10878 round 2: "can this row render" is the weaker of the two questions,
// and answering only that one still lost the bump. A synthetic edge puts the
// referenced Chat inside the cached `listChatsPagination` read, so EVERY field
// that query selects must already be on the entity or the read goes
// incomplete — and ChatsData's cache-first watchQuery then falls through to
// the network, whose response does not yet carry this activity and overwrites
// the bump. Adding `cursor` to the synthetic edge fixed the edge half of that
// read; this is the node half. A chat cached only by `GetChatDetail` (search
// result, deep link, push open) is the live case: it has name / avatarUrl /
// isGroupChat / createdAt but no `friendRequestAccepted`, `userIds`,
// `settingsPin`, `settingsMute`, `canCurrentUserDecide`, `settingsArchivedAt`,
// `ownerId` or `ownerName`, so the old four-field check waved it through and
// the prepend itself was what broke the read.
//
// The requirement is therefore the query's own node selection, read off the
// generated document rather than restated here — a field added to
// `listChatsPagination.graphql` tightens this check on the next codegen run
// instead of silently reopening the hole. `id` is excluded because a reference
// always carries it (a dangling ref reads back `undefined` for every other
// field, so it still fails the check). `recentActivityAt` is NOT excluded,
// despite being the field a bump always tries to write: `cache.modify` cannot
// ADD an absent field, so on a thinly cached Chat that write is a no-op and
// the field stays missing — exactly the record that must not be prepended.
//
// Known limit: `readField` proves a field is present, not that a nested
// selection under it is complete. `pendingInvitations { email }` passes as
// soon as the list itself is cached.
const FALLBACK_LIST_ROW_FIELDS = [
  'name',
  'avatarUrl',
  'isGroupChat',
  'createdAt',
] as const;

const fieldNamed = (
  selectionSet: SelectionSetNode | undefined,
  name: string
): FieldNode | undefined =>
  selectionSet?.selections.find(
    (selection): selection is FieldNode =>
      selection.kind === 'Field' && selection.name.value === name
  );

// Takes the document as an argument rather than reaching for the import:
// this runs at module scope, and a test that partially mocks
// `@graphql/generated` (many do — they stub the hooks they use and nothing
// else) leaves `ListChatsPaginationDocument` undefined, which threw here and
// took down every suite that transitively imports this module. An absent or
// unrecognizable document falls back to the render-only rule instead.
export const listRowFieldsFromDocument = (
  document: DocumentNode | undefined
): readonly string[] => {
  // Not `document?.definitions.find`: a jest.mock of '@graphql/generated'
  // supplies whatever the suite needs and nothing else, so the export can be
  // present but not a document at all (`{}`, a jest.fn) as well as absent.
  const definitions = document?.definitions;
  const operation = Array.isArray(definitions)
    ? definitions.find(
        (definition): definition is OperationDefinitionNode =>
          definition.kind === 'OperationDefinition'
      )
    : undefined;
  const node = fieldNamed(
    fieldNamed(
      fieldNamed(operation?.selectionSet, 'listChatsPagination')?.selectionSet,
      'edges'
    )?.selectionSet,
    'node'
  );
  const fields = (node?.selectionSet?.selections ?? [])
    .filter((selection): selection is FieldNode => selection.kind === 'Field')
    .map((selection) => selection.name.value)
    .filter((name) => name !== '__typename' && name !== 'id');
  // An empty list would wave every chat through, which is the exact failure
  // this check exists to prevent; fall back to the render-only rule instead.
  return fields.length > 0 ? fields : FALLBACK_LIST_ROW_FIELDS;
};

export const REQUIRED_LIST_ROW_FIELDS = listRowFieldsFromDocument(
  ListChatsPaginationDocument as DocumentNode | undefined
);
// TA-10875: how long after a local activity bump an older `listChats`
// response is treated as the losing side of the race rather than as new
// truth. Covers a query already in flight when the send happened; anything
// landing later is authoritative and must be allowed to lower the value,
// including a server-side decrease (deleting a chat's newest message) and
// the correction for a bump written from a device clock running fast.
const LOCAL_BUMP_GUARD_MS = 15_000;

type GuardedActivity = {
  chatId: string;
  // When the *bump* happened. Moving the guard onto a newer value does not
  // extend the window, so it can never outlive the race it exists for.
  bumpedAt: number;
};

// Keyed by the guarded `recentActivityAt` value, because that is all a field
// merge function can see: it gets `existing`/`incoming` but no handle on the
// entity they belong to (`storeObject` and `readField('id')` are both
// undefined there). The chat id is carried along so a value claimed by two
// chats can be detected and the guard disarmed — see guardedChatIdFor.
const guardsByActivityValue = new Map<string, GuardedActivity>();

// Values seen claimed by more than one chat, with the time the collision was
// detected. A collision disarms the value for a whole window rather than only
// while both guards are live: dropping just the guards would let the next
// bump — or the surviving one, once its rival expires — rearm a value already
// known not to identify a single chat, and suppress a legitimate older write
// for the other chat.
const ambiguousSince = new Map<string, number>();

const isExpired = (at: number, now: number) => now - at >= LOCAL_BUMP_GUARD_MS;

const pruneExpiredGuards = (now: number) => {
  guardsByActivityValue.forEach(({ bumpedAt }, value) => {
    if (isExpired(bumpedAt, now)) guardsByActivityValue.delete(value);
  });
  ambiguousSince.forEach((at, value) => {
    if (isExpired(at, now)) ambiguousSince.delete(value);
  });
};

/**
 * The chat whose locally bumped `recentActivityAt` equals `value`, while the
 * race window for that bump is still open.
 *
 * Returns undefined when no live guard holds the value, and when the value is
 * ambiguous: timestamps are not a per-chat identity, so a value two chats have
 * claimed disarms rather than risking a pin on the wrong chat. Failing open
 * costs at most the original race for a chat that happens to share a
 * millisecond-precision timestamp with another chat's send.
 */
export const guardedChatIdFor = (value: string): string | undefined => {
  const now = Date.now();

  const ambiguousAt = ambiguousSince.get(value);
  if (ambiguousAt !== undefined) {
    if (!isExpired(ambiguousAt, now)) return undefined;
    ambiguousSince.delete(value);
  }

  const guard = guardsByActivityValue.get(value);
  if (!guard) return undefined;
  if (isExpired(guard.bumpedAt, now)) {
    guardsByActivityValue.delete(value);
    return undefined;
  }
  return guard.chatId;
};

/**
 * Claims `value` for a chat. A second chat claiming a value another chat
 * already holds marks it ambiguous for the rest of the window — see
 * ambiguousSince.
 */
const registerGuard = (value: string, guard: GuardedActivity) => {
  const now = Date.now();
  const ambiguousAt = ambiguousSince.get(value);
  if (ambiguousAt !== undefined) {
    if (!isExpired(ambiguousAt, now)) return;
    ambiguousSince.delete(value);
  }

  const existing = guardsByActivityValue.get(value);
  if (
    existing &&
    !isExpired(existing.bumpedAt, now) &&
    existing.chatId !== guard.chatId
  ) {
    guardsByActivityValue.delete(value);
    ambiguousSince.set(value, now);
    return;
  }
  guardsByActivityValue.set(value, guard);
};

/**
 * Moves a live guard from `previousValue` onto a newer value accepted from
 * the server, keeping the original expiry. Without this, a list response that
 * legitimately advances the timestamp would disarm the guard, letting the
 * *original* pre-send response — still in flight, still older — win if it
 * lands afterwards.
 */
export const advanceGuardedActivity = (
  previousValue: string,
  nextValue: string
) => {
  const guard = guardsByActivityValue.get(previousValue);
  if (!guard || previousValue === nextValue) return;
  guardsByActivityValue.delete(previousValue);
  registerGuard(nextValue, guard);
};

// TA-9295: the `recentActivityAt` each chat was last bumped to from THIS
// device's clock (the send path), as opposed to a server value. Lets the
// chatUpdate handler tell a cached value it cannot compare server timestamps
// against from one it can. Exact-value match, so it needs no expiry: once any
// other value reaches the cache — a server echo, a list refetch — the entry
// no longer matches. Bounded by the number of chats sent into this session;
// not persisted, so after a restart a cached device value reads as
// authoritative, which is the pre-TA-9295 behaviour.
const localClockActivityByChat = new Map<string, string>();

/**
 * True when `value` is the device-clock timestamp this client's own send
 * wrote for `chatId` — see localClockActivityByChat.
 */
export const isLocalClockActivity = (
  chatId: string,
  value: string | null | undefined
): boolean => !!value && localClockActivityByChat.get(chatId) === value;

/** Test seam — clears the guard window state between cases. */
export const resetLocalActivityBumps = () => {
  guardsByActivityValue.clear();
  ambiguousSince.clear();
  localClockActivityByChat.clear();
};
export type ChatActivityBump = {
  chatId: string;
  recentActivityAt: string;
  // TA-9295: true when `recentActivityAt` came from the device clock (the
  // optimistic send) rather than the server — see isLocalClockActivity.
  localClock?: boolean;
  messageBody?: string | null;
  unreadCount?: number | null;
  // TA-10878: the row renders this next to the preview text. Pass it whenever
  // the caller knows what the newest message carries — including `null` for a
  // plain text message, which is what clears a stale paperclip/photo icon
  // left behind by the previous attachment message.
  messageIcon?: string | null;
};

/**
 * True when a `listChatsPagination` store field belongs to the archived list.
 *
 * TA-10878: `cache.modify`'s modifier details carry no `args` — Apollo's
 * `ModifierDetails` is
 * `{ DELETE, INVALIDATE, fieldName, storeFieldName, readField, canRead,
 * isReference, toReference, storage }` — so the previous `args?.archivesOnly`
 * guard was always `undefined` and never fired, letting an off-page chat be
 * prepended into the archived connection. `storeFieldName` is the field key
 * Apollo actually stores under, built from `keyArgs: ['archivesOnly']`:
 * `listChatsPagination:{}` for the active list,
 * `listChatsPagination:{"archivesOnly":true}` for the archive.
 */
export const isArchivedChatsField = (storeFieldName?: string): boolean => {
  if (!storeFieldName) return false;
  const argsStart = storeFieldName.indexOf('{');
  if (argsStart === -1) return false;
  try {
    return JSON.parse(storeFieldName.slice(argsStart))?.archivesOnly === true;
  } catch {
    // An unparseable key is not provably the archive list; treating it as the
    // active one keeps the bump working rather than silently dropping it.
    return false;
  }
};

/**
 * True when the chat already has an edge in the cached ACTIVE
 * `listChatsPagination` connection.
 *
 * TA-10878: the subscription handler drops an event older than what the cache
 * holds, which is routinely the echo of the user's OWN send — bumpChatToTop
 * writes a client-clock timestamp that the server's is a moment behind. That
 * is the right call for the field writes, but it also skipped the fallback
 * refetch, so a chat that was never placed in the list (below the first page,
 * guard refused) had nothing left to bring it in.
 *
 * Implemented as a no-op `cache.modify`: every modifier returns the
 * connection it was given, so nothing is dirtied and no watcher fires. It is
 * the only way to see the store field under its real key — the connection is
 * stored per `keyArgs: ['archivesOnly']` and a read needs variables to match.
 */
export const isChatInCachedList = (
  cache: ApolloCache<any>,
  chatId: string
): boolean => {
  let found = false;
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      listChatsPagination(existingConn: any, details: any) {
        const { readField, storeFieldName } = details;
        if (isArchivedChatsField(storeFieldName)) return existingConn;
        found =
          found ||
          (existingConn?.edges ?? []).some(
            (edge: any) => readField('id', edge?.node) === chatId
          );
        return existingConn;
      },
    },
  });
  return found;
};

/**
 * Writes the new activity fields onto a Chat and moves it to the front of
 * the cached `listChatsPagination` edges. Shared by the `chatUpdate`
 * subscription handler (useChatInput.tsx) and the optimistic send-message
 * path (useSendMessage.ts) — TA-10383: the sender's own outgoing message
 * must not depend solely on the subscription round-trip to reorder the
 * chat list, since that socket event can be dropped/delayed on iOS.
 *
 * Returns true ONLY when the chat was verifiably placed into a cached
 * connection — moved to the front of one it is already in, or prepended as a
 * synthetic edge. Every other path returns false so the caller falls back to
 * refetching `listChatsPagination`: the chat still surfaces, just via the
 * network instead of a synthetic edge.
 *
 * TA-10876: this used to return `!skippedUnrenderable` — "nothing actively
 * refused to place it" — which reads as success on the paths where the
 * modifier never ran at all. `cache.modify` only invokes a field modifier for
 * fields that already exist on the store object, so when ROOT_QUERY has no
 * `listChatsPagination` field (the initial list query never produced a result:
 * an HTTP 500, an offline blip, an auth hiccup on first load) the modifier
 * below is never called, the old return said `true`, and the caller skipped
 * its network fallback. The chat list then stayed dark for the rest of the
 * session and only a reload recovered it.
 */
export const bumpChatToTop = (
  cache: ApolloCache<any>,
  {
    chatId,
    recentActivityAt,
    messageBody,
    unreadCount,
    messageIcon,
    localClock,
  }: ChatActivityBump
): boolean => {
  const chatCacheId = cache.identify({ __typename: 'Chat', id: chatId });
  if (!chatCacheId) return false;

  if (localClock) localClockActivityByChat.set(chatId, recentActivityAt);
  else localClockActivityByChat.delete(chatId);

  const bumpedAt = Date.now();
  // Prune on insert: a bump whose value never reaches a later merge would
  // otherwise sit in the map for the rest of the session.
  pruneExpiredGuards(bumpedAt);
  registerGuard(recentActivityAt, { chatId, bumpedAt });

  cache.modify({
    id: chatCacheId,
    fields: {
      recentActivityAt: () => recentActivityAt,
      ...(messageBody !== undefined ? { messageBody: () => messageBody } : {}),
      ...(unreadCount !== undefined ? { unreadCount: () => unreadCount } : {}),
      ...(messageIcon !== undefined ? { messageIcon: () => messageIcon } : {}),
    },
  });

  // Set by the field function below only on the paths that actually write the
  // chat into a connection. It stays false when the modifier never runs (no
  // `listChatsPagination` field on ROOT_QUERY) and on every early return
  // inside it. Read after cache.modify returns — Apollo runs field functions
  // synchronously.
  let placedInList = false;

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      listChatsPagination(existingConn: any, details: any) {
        const { readField, toReference, storeFieldName } = details;
        if (!existingConn?.edges) return existingConn;

        const edges = existingConn.edges.slice();
        const ref = toReference({ __typename: 'Chat', id: chatId });
        if (!ref) return existingConn;

        const idx = edges.findIndex(
          (e: any) => readField('id', e?.node) === chatId
        );
        if (idx !== -1) {
          // Chat is already in the list — move it to the front.
          const [edge] = edges.splice(idx, 1);
          edges.unshift(edge);
          placedInList = true;
          return { ...existingConn, edges };
        } else {
          // Only add to the active (non-archived) list.
          if (isArchivedChatsField(storeFieldName)) return existingConn;
          // TA-10627 / TA-10878: never prepend a Chat the cache cannot both
          // render AND read back completely (see REQUIRED_LIST_ROW_FIELDS).
          // Returning the connection untouched leaves `placedInList` false,
          // so the caller refetches and the chat surfaces over the network —
          // in its real sort position, with every field the row needs. That
          // is strictly better than a synthetic edge that renders for one
          // frame and is then overwritten by the very network read its own
          // incompleteness triggered.
          const canPlaceInList = REQUIRED_LIST_ROW_FIELDS.every(
            (field) => readField(field, ref) !== undefined
          );
          if (!canPlaceInList) return existingConn;
          // TA-10878: `cursor` is in the query's selection set
          // (listChatsPagination.graphql: `edges { cursor node { ... } }`), so
          // an edge without one makes the cached read incomplete and ChatsData's
          // cache-first watchQuery falls through to the network — whose
          // response does not yet carry this activity and overwrites the bump.
          // Empty string keeps the read complete while staying inert in
          // relayStylePagination: its read() only feeds truthy edge cursors
          // into pageInfo.{start,end}Cursor, and its merge() only matches
          // truthy `args.after`/`args.before` against edge cursors. A real
          // cursor for this position exists only server-side.
          const newEdge = {
            __typename: 'ChatEdge',
            cursor: '',
            node: ref,
          };
          placedInList = true;
          return { ...existingConn, edges: [newEdge, ...edges] };
        }
      },
    },
  });

  return placedInList;
};
